import type { DxfEntityKey } from '@frontend/api';
import {
  fitAabb,
  isPositiveFinite,
  unionAabb,
  type Aabb2,
  type Point2,
  type QuarterTurns,
  type ViewportSize,
  type ViewportState,
} from '@frontend/viewer-core';

import {
  FEATURE_SNAP_DURATION_MS,
  FEATURE_SNAP_MAX_ZOOM,
  FEATURE_SNAP_MIN_DURATION_MS,
} from '../constants';
import type { DrawingModel } from './types';

/** One gap segment in drawing units. Same shape as a geometric-analysis span. */
export type FrameSpan = readonly [Point2, Point2];

const boundsOfSpan = (span: FrameSpan): Aabb2 => {
  const [[x1, y1], [x2, y2]] = span;
  return [
    Math.min(x1, x2),
    Math.min(y1, y2),
    Math.max(x1, x2),
    Math.max(y1, y2),
  ];
};

/**
 * World bounds to frame for an interrogation item.
 *
 * Chunk bounds are the DXF stand-in for the face and edge boxes the CAD snap
 * zooms to. Gap spans are included too: a clearance is the space between
 * entities, so the segment is often the thing to look at. Unstitched entities
 * (text, hatches, points) contribute nothing.
 */
export const entityFrameBounds = (
  model: DrawingModel | null,
  keys: readonly DxfEntityKey[],
  spans: readonly FrameSpan[] = [],
): Aabb2 | null => {
  if (!model) {
    return null;
  }

  let bounds: Aabb2 | null = null;
  for (const key of keys) {
    const addresses = model.chunksByEntityKey.get(key);
    if (!addresses) {
      continue;
    }
    for (const { loopId, chunkIndex } of addresses) {
      const chunk = model.loopsById.get(loopId)?.chunks[chunkIndex];
      if (!chunk) {
        continue;
      }
      bounds = unionAabb(bounds, chunk.bounds);
    }
  }

  for (const span of spans) {
    bounds = unionAabb(bounds, boundsOfSpan(span));
  }

  return bounds;
};

/**
 * Viewport that centers on `featureBounds` and zooms in to frame it.
 *
 * Zoom is the fit of those bounds, and never more than `FEATURE_SNAP_MAX_ZOOM`
 * times a fit of the whole drawing — a hairline feature stops at that cap
 * instead of filling the screen. A point has no extent to fit, so it takes the
 * cap and stays centred on the point.
 */
export const featureViewport = (
  featureBounds: Aabb2,
  drawingBounds: Aabb2 | null,
  size: ViewportSize,
  quarterTurns: QuarterTurns,
): ViewportState => {
  const featureFit = fitAabb(featureBounds, size, { quarterTurns });
  const drawingScale = fitAabb(drawingBounds, size, { quarterTurns }).scale;
  const maxScale = drawingScale * FEATURE_SNAP_MAX_ZOOM;
  const [minX, minY, maxX, maxY] = featureBounds;
  const hasExtent =
    isPositiveFinite(maxX - minX) || isPositiveFinite(maxY - minY);

  return {
    ...featureFit,
    scale: hasExtent ? Math.min(featureFit.scale, maxScale) : maxScale,
  };
};

/**
 * Linear blend from `start` toward `target`. `t` is clamped to `0..1`.
 *
 * Rotation is not blended: a feature focus keeps the current quarter turn, and
 * a partial turn has no representation in this viewport.
 */
export const lerpViewport = (
  start: ViewportState,
  target: ViewportState,
  t: number,
): ViewportState => {
  const clamped = Math.min(1, Math.max(0, t));
  return {
    center: [
      start.center[0] + (target.center[0] - start.center[0]) * clamped,
      start.center[1] + (target.center[1] - start.center[1]) * clamped,
    ],
    scale: start.scale + (target.scale - start.scale) * clamped,
    quarterTurns: target.quarterTurns,
  };
};

/**
 * How long to lerp from `start` to `target`.
 *
 * The fraction is the larger of the pan and the zoom. The pan is how far the
 * centre travels across the container, measured at the closer of the two zoom
 * levels, so a small gap between callouts is a short move. The zoom is the
 * relative scale change. A fraction of 1 — a pan across the view, or further —
 * takes `FEATURE_SNAP_DURATION_MS`; anything quieter stops at
 * `FEATURE_SNAP_MIN_DURATION_MS`. An unmeasured container keeps the ceiling,
 * since the pan has no screen length yet.
 */
export const featureSnapDuration = (
  start: ViewportState,
  target: ViewportState,
  size: ViewportSize,
): number => {
  const span = Math.hypot(size.width, size.height);
  const worldDistance = Math.hypot(
    target.center[0] - start.center[0],
    target.center[1] - start.center[1],
  );
  const panRatio = isPositiveFinite(span)
    ? (worldDistance * Math.max(start.scale, target.scale)) / span
    : 1;

  const peakScale = Math.max(start.scale, target.scale);
  const zoomRatio = isPositiveFinite(peakScale)
    ? Math.abs(target.scale - start.scale) / peakScale
    : 0;

  const travel = Math.min(1, Math.max(0, panRatio, zoomRatio));
  return Math.max(
    FEATURE_SNAP_MIN_DURATION_MS,
    FEATURE_SNAP_DURATION_MS * travel,
  );
};
